import {defineStore} from "pinia";
import {computed, Ref, ref} from "vue";
import {AxiosResponse} from "axios";
import {catalogoApi} from "@/requests/catalogo";
import {IBasePaginateResponse} from "@/interfaces/response/IBasePaginateResponse";
import {ICatalogoIstanze} from "@/interfaces/response/catalogoIstanze/ICatalogoIstanze";
import {ICatalogoIstanzeList} from "@/interfaces/response/catalogoIstanze/ICatalogoIstanzeList";
import {IDestinatariList} from "@/interfaces/response/destinatari/IDestinatariList";
import {IDestinatari} from "@/interfaces/response/destinatari/IDestinatari";
import {IArgomentiList} from "@/interfaces/response/argomenti/IArgomentiList";
import {IArgomenti} from "@/interfaces/response/argomenti/IArgomenti";
import {ITipologieList} from "@/interfaces/response/tipologie/ITipologieList";
import {ITipologie} from "@/interfaces/response/tipologie/ITipologie";
import {IService, IServiceFilterIn, IServiceFilterOut} from "cdp-fe-commons-layout";
import {IMaterieList} from "@/interfaces/response/materie/IMaterieList";
import {IMaterie} from "@/interfaces/response/materie/IMaterie";
import { createCatalogoAxiosInstance } from '@/axios/Catalogo'

export const useServiceStore = defineStore('service', () => {

    //State
    const services = ref<IService[]>([])
    const servicesCount = ref<number>(0)
    const serviceFilters = ref<IServiceFilterIn>();
    const catalogoAxios = createCatalogoAxiosInstance();

    //getters
    const servicesData = computed<Ref<IService[]>>(() => services);
    const servicesDataCount = computed<Ref<number>>(() => servicesCount);
    const serviceFiltersData = computed<Ref<IServiceFilterIn|undefined>>(() => serviceFilters);

    async function getServiceTitle(codiceCatalogo: string): Promise<string>{
        return catalogoAxios.get<ICatalogoIstanze>(catalogoApi.GET_TITOLO_CATALOGO_ISTANZE_BY_CODICE_CATALOGO(codiceCatalogo))
            .then((el: AxiosResponse<ICatalogoIstanze>) => {
                return el.data.titolo ?? codiceCatalogo
            }).catch(() => codiceCatalogo)
    }

    //Actions
    async function requestServices(page: number, limit: number, filters?: IServiceFilterOut){
        catalogoAxios.get<IBasePaginateResponse<ICatalogoIstanzeList>>(catalogoApi.GET_ALL_CATALOGO_ISTANZE(page, limit, filters))
            .then((el: AxiosResponse<IBasePaginateResponse<ICatalogoIstanzeList>>) => {
            servicesCount.value = el.data.page.totalElements;
            services.value = el.data._embedded.catalogoIstanze.map((el: ICatalogoIstanze) => {
                return {
                    id: el.catalogoId,
                    title: el.titolo,
                    description: el.descrizioneBreve,
                    dataInizioPresentazione: el.presentazioneInizio,
                    dataFinePresentazione: el.presentazioneFine,
                    pubblicato: el.pubblicato,
                    inviabile: el.inviabile,
                    argomento: el.descrizioneArgomento,
                    routeLink:'/servizi/dettaglio/'+el.codiceCdp,
                    svgMaterie: el.svgMateria,
                    argomentoRoute: '',
                    categoriaTipologiaProcedimento: el.categoriaTipologiaProcedimento,
                    codeTipologiaProcedimento: el.codeTipologiaProcedimento
                } as IService;
            });
        });
    }

    async function requestServicesInterni(page: number, limit: number, filters?: IServiceFilterOut){
        catalogoAxios.get<IBasePaginateResponse<ICatalogoIstanzeList>>(catalogoApi.GET_ALL_CATALOGO_ISTANZE_INTERNI(page, limit, filters))
            .then((el: AxiosResponse<IBasePaginateResponse<ICatalogoIstanzeList>>) => {
                servicesCount.value = el.data.page.totalElements;
                services.value = el.data._embedded.catalogoIstanze.map((el: ICatalogoIstanze) => {
                    return {
                        id: el.catalogoId,
                        title: el.titolo,
                        description: el.descrizioneBreve,
                        dataInizioPresentazione: el.presentazioneInizio,
                        dataFinePresentazione: el.presentazioneFine,
                        pubblicato: el.pubblicato,
                        inviabile: el.inviabile,
                        argomento: el.descrizioneArgomento,
                        routeLink:'/servizi-interni/dettaglio/'+el.codiceCdp,
                        svgMaterie: el.svgMateria,
                        argomentoRoute: '',
                        categoriaTipologiaProcedimento: el.categoriaTipologiaProcedimento,
                        codeTipologiaProcedimento: el.codeTipologiaProcedimento
                    } as IService;
                });
            });
    }

    function getArgomentiByMateria(materiaId: string|undefined){
        let url = catalogoApi.GET_ALL_ARGOMENTI();
        if(materiaId && materiaId !== ''){
            url = catalogoApi.GET_ALL_ARGOMENTI_BY_MATERIA_ID(materiaId);
        }
        catalogoAxios.get<IBaseResponse<IArgomentiList>>(url)
            .then(el => {
                const argomenti = el.data._embedded.argomenti.map((p: IArgomenti) => {
                    return {value: p.argomentiId, label: `${p.titoloMateria} - ${p.descrizione}`}
                });
                if(serviceFilters.value){
                    serviceFilters.value.argomenti = argomenti;
                }
            })
    }

    function requestServiceFilters() {
        const endpoints = [
            catalogoAxios.get(catalogoApi.GET_ALL_ARGOMENTI()),
            catalogoAxios.get(catalogoApi.GET_ALL_DESTINATARI()),
            catalogoAxios.get(catalogoApi.GET_ALL_TIPOLOGIE()),
            catalogoAxios.get(catalogoApi.GET_ALL_MATERIE()),
            catalogoAxios.get(catalogoApi.GET_ALL_STRUTTURE()),
        ];

        Promise.all(endpoints).then((response) => {
            const argomentiResponse = response[0]?.data as IBaseResponse<IArgomentiList>;
            const destinatariResponse = response[1]?.data as IBaseResponse<IDestinatariList>;
            const tipologieResponse = response[2]?.data as IBaseResponse<ITipologieList>;
            const materieResponse = response[3]?.data as IBaseResponse<IMaterieList>;
            const struttureResponse = response[4]?.data as any;

            const argomenti = argomentiResponse._embedded.argomenti.map((p: IArgomenti) => {
                return {value: p.argomentiId, label: `${p.titoloMateria} - ${p.descrizione}`}
            });
            const destinatari = destinatariResponse._embedded.destinatari.map((p: IDestinatari) => {
                return {value: p.destinatariId, label: p.descrizione}
            });
            const tipologie = tipologieResponse._embedded.tipologie.map((p: ITipologie) => {
                return {value: p.tipologieId, label: p.descrizione}
            });
          const materie = materieResponse._embedded.materie.map((p: IMaterie) => {
            return {
              value: p.materieId,
              label: p.descrizione,
              svg: p.svg,
            }
          });
          const strutture = struttureResponse.map((p: any) => {
            return {value: p.idStruttura, label: p.codiceStruttura + " - " + p.nomeStruttura}
          });

            serviceFilters.value = {
                destinatari: destinatari,
                argomenti: argomenti,
                tipologie: tipologie,
                materie: materie,
                strutture: strutture
            } as IServiceFilterIn;

        });
    }


    return {
        //Getters
        servicesData, servicesDataCount, serviceFiltersData,
        //Actions
        requestServices, requestServicesInterni, requestServiceFilters, getServiceTitle, getArgomentiByMateria
    }
})
